import { getAddress } from "viem";
import { signMessage } from "viem/accounts";

// web3 1.x compatibility for EthereumRpc (the viem migration).
// The login sends the wallet address and a message signature to consumer apps, so these reproduce what web3 1.10.4
// returned and threw for web3.eth.getAccounts() and web3.eth.accounts.sign(), quirks included, as the app ran it: web3's
// prebuilt browser bundle (dist/web3.min.js, which webpack picks over lib/ and which carries the npm `util` package).
// web3Compat.test.js and web3RPC.test.js compare them with that bundle.
// Keep config imports out of this file so the tests can load it.

// web3-core-helpers errors
const connectionError = (message, event) => {
    const error = new Error(message);
    if (event) {
        error.code = event.code;
        error.reason = event.reason;
        if (event.description) {
            error.description = event.description;
        }
    }
    return error;
};

const invalidResponse = (result) => new Error(!!result && !!result.error && !!result.error.message ? result.error.message : 'Invalid JSON RPC response: ' + JSON.stringify(result));

const errorResponse = (result) => {
    const message = !!result && !!result.error && !!result.error.message ? result.error.message : JSON.stringify(result);
    const error = new Error('Returned error: ' + message);
    error.data = (!!result.error && !!result.error.data) ? result.error.data : null;
    return error;
};

// web3-core-requestmanager Jsonrpc: ids start at a random number and count up
let messageId = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);

const toPayload = (method, params) => {
    if (messageId === Number.MAX_SAFE_INTEGER) {
        messageId = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
    } else {
        messageId++;
    }
    return { jsonrpc: '2.0', id: messageId, method, params: params || [] };
};

const isValidMessage = (message) => !!message &&
    !message.error &&
    message.jsonrpc === '2.0' &&
    (typeof message.id === 'number' || typeof message.id === 'string') &&
    message.result !== undefined;

const isValidResponse = (response) => Array.isArray(response) ? response.every(isValidMessage) : isValidMessage(response);

// web3-core-requestmanager _jsonrpcResultCallback: how a provider without request() had its response checked
const jsonrpcResultCallback = (callback, payload) => (err, result) => {
    if (result && result.id && payload.id !== result.id) {
        return callback(new Error(`Wrong response id ${result.id} (expected: ${payload.id}) in ${JSON.stringify(payload)}`));
    }
    if (err) {
        return callback(err);
    }
    if (result && result.error) {
        return callback(errorResponse(result));
    }
    if (!isValidResponse(result)) {
        return callback(invalidResponse(result));
    }
    callback(null, result.result);
};

// web3-providers-http HttpProvider with the options EthereumRpc used (none: no timeout, no extra headers, credentials
// omitted). EthereumRpc falls back to it without a wallet provider. Like web3's, it has send() and no request().
export class LegacyHttpProvider {
    constructor(host) {
        this.host = host || 'http://localhost:8545';
    }

    send(payload, callback) {
        const host = this.host;
        fetch(host, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: { 'Content-Type': 'application/json' },
            credentials: 'omit',
        }).then(
            (response) => response.json().then(
                (data) => callback(null, data),
                // json() has already read the body, so text() fails too and the message is 'Invalid JSON RPC response: ""'
                () => response.text().then((text) => callback(invalidResponse(text)), () => callback(invalidResponse(''))),
            ),
            (error) => {
                if (error.name === 'AbortError') {
                    callback(new Error('CONNECTION TIMEOUT: timeout of 0 ms achived'));
                    return;
                }
                callback(connectionError('CONNECTION ERROR: Couldn\'t connect to node ' + host + '.', error));
            },
        );
    }
}

// web3-core-requestmanager RequestManager.send. A provider with request() is called through util.callbackify: a
// synchronous throw escapes as it is (the returned promise rejects with it here), and a rejection with a falsy value
// becomes an Error. Otherwise sendAsync() or send() gets a JSON-RPC payload. Resolves to [error, result].
const legacySend = (provider, method, params) => new Promise((resolve) => {
    const callback = (error, result) => resolve([error, result]);
    if (!provider) {
        callback(new Error('Provider not set or invalid'));
        return;
    }
    if (provider.request) {
        provider.request({ method, params }).then(
            (result) => callback(null, result),
            (reason) => {
                if (!reason) {
                    const error = new Error('Promise was rejected with a falsy value');
                    error.reason = reason;
                    reason = error;
                }
                callback(reason);
            },
        );
    } else if (provider.sendAsync) {
        const payload = toPayload(method, params);
        provider.sendAsync(payload, jsonrpcResultCallback(callback, payload));
    } else if (provider.send) {
        const payload = toPayload(method, params);
        provider.send(payload, jsonrpcResultCallback(callback, payload));
    } else {
        throw new Error('Provider does not have a request or send method to use.');
    }
});

// web3-utils toChecksumAddress: no 0x needed, and an existing checksum is not validated
const toChecksumAddress = (address) => {
    if (typeof address === 'undefined') {
        return '';
    }
    if (!/^(0x)?[0-9a-f]{40}$/i.test(address)) {
        throw new Error('Given address "' + address + '" is not a valid Ethereum address.');
    }
    return getAddress('0x' + address.toLowerCase().replace(/^0x/i, ''));
};

// web3-utils _fireError: an object with `data` becomes Error("<message>\n<data>") (object data as indented JSON),
// a string becomes an Error, anything else is rejected as it is
const legacyFiredError = (error) => {
    if (!!error && typeof error === 'object' && !(error instanceof Error) && error.data) {
        if ((!!error.data && typeof error.data === 'object') || Array.isArray(error.data)) {
            error.data = JSON.stringify(error.data, null, 2);
        }
        error = error.message + "\n" + error.data;
    }
    if (typeof error === 'string') {
        error = new Error(error);
    }
    return error;
};

// web3.eth.getAccounts(): eth_accounts with no params, every truthy entry checksummed (a non-array result as a whole);
// failures reshaped as web3-core-method did before rejecting
export const legacyGetAccounts = (provider) => legacySend(provider, 'eth_accounts', []).then(([err, result]) => {
    let error = err;
    let accounts = result;
    try {
        accounts = Array.isArray(result)
            ? result.map((account) => account ? toChecksumAddress(account) : account)
            : result ? toChecksumAddress(result) : result;
    } catch (e) {
        error = e;
    }
    if (accounts instanceof Error) {
        error = accounts;
    }
    if (!error) {
        return accounts;
    }
    if (error.error) {
        error = error.error;
    }
    throw legacyFiredError(error);
});

// web3-utils hexToBytes on hex without its 0x: two digits at a time from the left, so an odd last digit stands alone;
// the Uint8Array turns NaN (from a "-0x" prefix) into 0, as Buffer.from did
const legacyHexDigitsToBytes = (digits) => {
    const bytes = new Uint8Array(Math.ceil(digits.length / 2));
    for (let i = 0; i < digits.length; i += 2) {
        bytes[i / 2] = parseInt(digits.slice(i, i + 2), 16);
    }
    return bytes;
};

// utf8.encode, the encoder web3-utils uses: like TextEncoder, except that a lone surrogate throws instead of becoming U+FFFD
const toLegacyUtf8Bytes = (text) => {
    const bytes = [];
    for (let i = 0; i < text.length; i++) {
        let code = text.charCodeAt(i);
        if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
            const low = text.charCodeAt(i + 1);
            if ((low & 0xfc00) === 0xdc00) {
                code = ((code & 0x3ff) << 10) + (low & 0x3ff) + 0x10000;
                i++;
            }
        }
        if (code >= 0xd800 && code <= 0xdfff) {
            throw new Error(`Lone surrogate U+${code.toString(16).toUpperCase()} is not a scalar value`);
        }
        if (code < 0x80) {
            bytes.push(code);
        } else if (code < 0x800) {
            bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
        } else if (code < 0x10000) {
            bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        } else {
            bytes.push(0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        }
    }
    return bytes;
};

// The bytes web3-eth-accounts hashMessage signs: a 0x string (web3-utils isHexStrict, which also accepts -0x) is read as
// hex; anything else is UTF-8 with NUL characters trimmed from both ends (utf8ToHex)
const legacyMessageBytes = (message) => {
    if ((typeof message === 'string' || typeof message === 'number') && /^(-)?0x[0-9a-f]*$/i.test(message)) {
        return legacyHexDigitsToBytes(message.replace(/^0x/i, ''));
    }
    const bytes = toLegacyUtf8Bytes(message);
    let start = 0;
    let end = bytes.length;
    while (start < end && bytes[start] === 0) {
        start++;
    }
    while (end > start && bytes[end - 1] === 0) {
        end--;
    }
    return Uint8Array.from(bytes.slice(start, end));
};

// web3.eth.accounts.sign(message, privateKey).signature: an EIP-191 personal message signature, deterministic (RFC 6979)
// in both eth-lib and viem, so the same key and message give the same signature
export const signLegacyMessage = (message, privateKey) => {
    // In web3's browser bundle this parameter is `t`, so a key with no startsWith (the Error getPrivateKey returns when
    // there is no wallet provider) failed with that name in the message
    if (privateKey !== undefined && privateKey !== null && typeof privateKey.startsWith !== 'function') {
        throw new TypeError('t.startsWith is not a function');
    }
    if (!privateKey.startsWith('0x')) {
        privateKey = '0x' + privateKey;
    }
    // 64 hex characters + hex-prefix
    if (privateKey.length !== 66) {
        throw new Error("Private key must be 32 bytes long");
    }
    return signMessage({ message: { raw: legacyMessageBytes(message) }, privateKey });
};
